import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import sendMessage from './useSendComment.request';

import useZDUser from 'services/help-center/queries/useZDUser';
import {
    ApiResponse,
    Ticket,
} from 'modules/IMC/MessageCenter/components/MessageCenterContent/MessageList/MessageList.interface';

interface UseSendMessageProps {
    ticketId: number;
    message: string;
    uploads?: string[];
    authorId?: number;
    otherPayload?: { [key: string]: string | { id: number; value: string }[] };
}

export default function useSendMessage() {
    const queryClient = useQueryClient();

    const { data: zdUser } = useZDUser();

    return useMutation({
        mutationFn: ({ ticketId, message, authorId, otherPayload = {}, uploads = [] }: UseSendMessageProps) =>
            sendMessage(ticketId, {
                ticket: {
                    comment: { html_body: message, public: true, author_id: authorId ?? zdUser?.id, uploads },
                    status: 'open',
                    ...otherPayload,
                },
            }),
        onMutate: async ({ ticketId, message, authorId, otherPayload = {} }) => {
            const previousComments = queryClient.getQueryData(['ticketComments', ticketId]);

            queryClient.setQueryData(['zdTicket', ticketId], (oldTicket: any) => {
                return {
                    ...oldTicket,
                    ticket: {
                        ...oldTicket.ticket,
                        status: 'open',
                        ...otherPayload,
                    },
                };
            });

            queryClient.setQueryData(['ticketComments', ticketId], (oldComments: any) => {
                return {
                    ...oldComments,
                    pages: [
                        {
                            comments: [
                                {
                                    html_body: message,
                                    plain_body: message,
                                    public: true,
                                    author_id: authorId ?? zdUser?.id,
                                    created_at: new Date().toISOString(),
                                },
                            ],
                            ...otherPayload,
                        },
                        ...(oldComments.pages ?? []),
                    ],
                };
            });

            return { previousComments };
        },
        onSuccess: async ({ ticket }, { message, uploads = [] }) => {
            // optimistcally updates the cache of messageCenterTickets keys if it finds the ticket
            const queryCache = queryClient.getQueryCache();
            const queries = queryCache.findAll({ queryKey: ['messageCenterTickets'] });
            const lastComment: Ticket['last_comment'] = {
                id: (ticket.last_comment?.id ?? 0) + 1,
                type: 'Comment',
                author_id: zdUser.id,
                body: message,
                html_body: message,
                plain_body: message,
                public: true,
                audit_id: ticket.last_comment?.audit_id ?? 0,
                attachments: uploads.map((upload) => ({
                    id: uuidv4(),
                    file_name: upload,
                    content_url: upload,
                })),
                via: {
                    channel: 'api',
                    source: {
                        from: {
                            id: zdUser.id,
                        },
                        to: {
                            id: zdUser.id,
                        },
                        rel: 'user',
                    },
                },
                created_at: new Date().toISOString(),
            };

            queries.forEach((query) => {
                const data = query.state.data as ApiResponse | undefined;
                if (data?.results) {
                    const updatedResults = data.results.map((t) =>
                        t.id === ticket.id ? { ...t, ...ticket, last_comment: lastComment } : t
                    );
                    queryClient.setQueryData(query.queryKey, { ...data, results: updatedResults });
                }
            });

            // because we need the attachments url to preview and download (after they are uploaded) we pull
            // the comments of the ticket again to have this zendesk values
            if (uploads.length > 0) {
                queryClient.invalidateQueries({
                    queryKey: ['ticketComments', ticket.id],
                });
            }

            queryClient.invalidateQueries({
                queryKey: ['zdTicket', ticket.id],
            });
        },
    });
}
